import { forwardRef } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { ButtonText } from 'src/components/ButtonText';
import { CustomTooltip } from 'src/components/CustomTooltip';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';

import { EditButton } from './components/EditButton';
import { SubmitButton } from './components/SubmitButton';
import { useStyles } from './useStyles';

export const DraftStepBlock = forwardRef(
  (
    {
      className,
      classes,
      step,
      title,
      titleSlot,
      optional,
      tag,
      subtitle,
      active,
      children,
      regenerateAlert,
      onSubmitClick,
      disabled,
      saving,
      fetching,
      onEditClick,
      submitError,
      submitTooltip,
      activated,
      forceDisabled,
      buttonTitle,
      extraButtonClick,
      extraButtonTitle,
      extraButtonSaving,
      onReset,
      resetHint,
      activeFooterSlot,
    },
    ref
  ) => {
    const styles = useStyles({ classes });

    return (
      <div
        className={cx(styles.root, className, { [styles.notActive]: !active })}
        ref={ref}
        data-testid={`step_${step}`}
      >
        {active && regenerateAlert}

        {titleSlot
          ? titleSlot
          : title && (
              <div className={styles.titleRow}>
                <CustomTypography
                  className={styles.title}
                  variant={TYPOGRAPHY_VARIANT.medium16}
                >
                  {title} {optional && <span className={styles.optional}>(optional)</span>}
                  {tag && <span className={styles.tag}>{tag}</span>}
                </CustomTypography>
                {onReset && active && (
                  <CustomTooltip title={resetHint}>
                    <ButtonText
                      className={styles.resetButton}
                      onClick={onReset}
                    >
                      Reset
                    </ButtonText>
                  </CustomTooltip>
                )}
              </div>
            )}

        {subtitle && (
          <CustomTypography
            className={styles.subtitle}
            variant={TYPOGRAPHY_VARIANT.regular12}
          >
            {subtitle}
          </CustomTypography>
        )}

        {children}

        {active && (extraButtonClick || onSubmitClick) && (
          <div className={styles.activeFooter}>
            <div className={styles.activeFooterSlot}>{activeFooterSlot}</div>

            <div className={styles.footerButtons}>
              {submitError && (
                <CustomTypography
                  className={styles.errorMessage}
                  variant={TYPOGRAPHY_VARIANT.regular12}
                >
                  {submitError}
                </CustomTypography>
              )}

              {extraButtonClick && (
                <SubmitButton
                  className={saving.submitButton}
                  onClick={extraButtonClick}
                  loading={extraButtonSaving}
                  disabled={fetching || disabled || forceDisabled || saving}
                  title={extraButtonTitle}
                  draft
                />
              )}

              {onSubmitClick && (
                <SubmitButton
                  className={saving.submitButton}
                  onClick={onSubmitClick}
                  loading={saving}
                  disabled={fetching || disabled || forceDisabled || extraButtonSaving}
                  title={buttonTitle}
                  tooltip={submitTooltip}
                />
              )}
            </div>
          </div>
        )}

        {!active && activated && onEditClick && (
          <EditButton
            className={styles.editButton}
            onClick={onEditClick}
            disabled={disabled}
          />
        )}
      </div>
    );
  }
);

DraftStepBlock.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  step: PropTypes.string,
  title: PropTypes.string,
  titleSlot: PropTypes.node,
  optional: PropTypes.bool,
  tag: PropTypes.string,
  subtitle: PropTypes.string,
  active: PropTypes.bool,
  children: PropTypes.node,
  regenerateAlert: PropTypes.node,
  onSubmitClick: PropTypes.func,
  disabled: PropTypes.bool,
  saving: PropTypes.bool,
  fetching: PropTypes.bool,
  onEditClick: PropTypes.func,
  submitError: PropTypes.string,
  submitTooltip: PropTypes.string,
  activated: PropTypes.bool,
  forceDisabled: PropTypes.bool,
  buttonTitle: PropTypes.string,
  extraButtonClick: PropTypes.func,
  extraButtonTitle: PropTypes.string,
  extraButtonSaving: PropTypes.bool,
  onReset: PropTypes.func,
  resetHint: PropTypes.string,
  activeFooterSlot: PropTypes.node,
};
DraftStepBlock.defaultProps = {
  className: '',
  classes: {},
  step: null,
  title: null,
  titleSlot: null,
  optional: false,
  tag: null,
  subtitle: null,
  active: false,
  children: null,
  regenerateAlert: null,
  onSubmitClick: null,
  disabled: false,
  saving: false,
  fetching: false,
  onEditClick: null,
  submitError: null,
  submitTooltip: null,
  activated: false,
  forceDisabled: false,
  buttonTitle: null,
  extraButtonClick: null,
  extraButtonTitle: null,
  extraButtonSaving: false,
  onReset: null,
  resetHint: '',
  activeFooterSlot: null,
};
